import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  DocumentIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  type DragEvent,
  lazy,
  Suspense,
  useCallback,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Input, Type } from '@/components/form/Input';
import { Popover } from '@/components/Popover';
import { Tooltip } from '@/components/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CapabilityParameter } from '@/hooks/useCapabilities';
import { useUploadFile } from '@/hooks/useFiles';

const MonacoEditor = lazy(() =>
  import('@/sections/attacks/templateRefinement/components/MonacoEditor').then(
    m => ({ default: m.MonacoEditor })
  )
);

const CLEAR_SENTINEL = '__CLEAR__';

interface ParameterInputProps {
  param: CapabilityParameter;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  readOnly?: boolean;
  isRequired?: boolean;
  onBlur?: (paramName: string) => void;
}

function isCommaSeparated(param: CapabilityParameter): boolean {
  return param.description?.toLowerCase().includes('comma-separated') ?? false;
}

interface MultiSelectDropdownProps {
  param: CapabilityParameter;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

function MultiSelectDropdown({
  param,
  value,
  onChange,
  error,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const options = param.options ?? [];
  const selected = value ? value.split(',').filter(Boolean) : [];

  const filtered = search
    ? options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggleOption = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter(s => s !== opt)
      : [...selected, opt];
    onChange(next.join(','));
  };

  const selectAll = () => onChange(options.join(','));
  const clearAll = () => onChange('');

  const allSelected = options.length > 0 && selected.length === options.length;

  const triggerLabel = (() => {
    if (selected.length === 0) return 'All plugins (default)';
    if (selected.length <= 2) return selected.join(', ');
    return `${selected.slice(0, 2).join(', ')} +${selected.length - 2} more`;
  })();

  return (
    <Popover
      open={open}
      setOpen={setOpen}
      matchContainerWidth
      className="p-0"
      placement="bottom-start"
      container={
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary flex w-full items-center justify-between rounded-[2px] border p-1.5 pr-2 pl-3 text-sm focus:ring-1 focus:outline-hidden ${error ? 'border-chariot-status-critical' : ''}`}
          aria-label={`${param.name} multi-select`}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span
            className={
              selected.length === 0
                ? 'text-chariot-text-secondary'
                : 'text-chariot-text-primary'
            }
          >
            {triggerLabel}
          </span>
          <ChevronDownIcon className="text-chariot-text-secondary size-4 shrink-0" />
        </button>
      }
    >
      <div className="flex w-full flex-col">
        {/* Search */}
        <div className="border-chariot-stroke border-b p-2">
          <div className="border-chariot-stroke bg-chariot-background-primary flex items-center gap-1.5 rounded border px-2 py-1">
            <MagnifyingGlassIcon className="text-chariot-text-secondary size-3.5 shrink-0" />
            <input
              type="text"
              placeholder="Search plugins..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-chariot-text-primary placeholder:text-chariot-text-secondary w-full bg-transparent text-sm outline-hidden"
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
        {/* Select All / Clear All */}
        <div className="border-chariot-stroke flex items-center justify-between border-b px-3 py-1.5">
          <button
            type="button"
            onClick={allSelected ? clearAll : selectAll}
            className="text-chariot-primary text-xs hover:underline"
          >
            {allSelected ? 'Clear All' : 'Select All'}
          </button>
          {selected.length > 0 && (
            <span className="text-chariot-text-secondary text-xs">
              {selected.length} selected
            </span>
          )}
        </div>
        {/* Options */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: 240 }}
          role="listbox"
          aria-label={`${param.name} options`}
          aria-multiselectable="true"
        >
          {filtered.length === 0 ? (
            <div className="text-chariot-text-secondary px-3 py-2 text-sm">
              No plugins found
            </div>
          ) : (
            filtered.map(opt => {
              const isChecked = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={isChecked}
                  onClick={() => toggleOption(opt)}
                  className={`hover:bg-chariot-background-secondary flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${isChecked ? 'text-chariot-text-primary' : 'text-chariot-text-secondary'}`}
                >
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded border ${isChecked ? 'bg-chariot-primary border-chariot-primary' : 'border-chariot-stroke'}`}
                  >
                    {isChecked && (
                      <svg
                        viewBox="0 0 10 8"
                        className="size-2.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M1 4l3 3 5-6" />
                      </svg>
                    )}
                  </span>
                  {opt}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Popover>
  );
}

function isPasswordField(param: CapabilityParameter): boolean {
  const checkText = (text: string | undefined) =>
    text?.toLowerCase().includes('password') ?? false;

  return checkText(param.name) || checkText(param.description);
}

function isYamlField(param: CapabilityParameter): boolean {
  return param.description?.toLowerCase().includes('yaml') ?? false;
}

function isBooleanParam(param: CapabilityParameter): boolean {
  if (param.type === 'bool') return true;
  const desc = param.description?.toLowerCase() ?? '';
  return desc.includes('true/false') || desc.includes('boolean');
}

function isIntParam(param: CapabilityParameter): boolean {
  return param.type === 'int';
}

function MultiSelectCheckboxes({
  options,
  value,
  onChange,
  paramName,
  isRequired,
  error,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  paramName: string;
  isRequired: boolean;
  error?: string;
}) {
  const selected = new Set(
    value
      ? value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
      : []
  );

  const handleToggle = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange(Array.from(next).join(','));
  };

  return (
    <div>
      <div
        className={`border-chariot-stroke bg-chariot-background-primary max-h-48 overflow-y-auto rounded-[2px] border p-2 ${error ? 'border-chariot-status-critical' : ''}`}
        role="group"
        aria-label={`${paramName} multi-select${isRequired ? ' (required)' : ''}`}
      >
        {options.map(opt => (
          <label
            key={opt}
            className="text-chariot-text-primary hover:bg-chariot-background-secondary flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm"
          >
            <input
              type="checkbox"
              checked={selected.has(opt)}
              onChange={() => handleToggle(opt)}
              className="accent-chariot-brand-primary"
            />
            <span className="truncate" title={opt}>
              {opt}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p className="text-chariot-status-critical mt-1 text-xs">{error}</p>
      )}
    </div>
  );
}

function FileUploadInput({
  param,
  value,
  onChange,
  error,
  isRequired,
}: Pick<
  ParameterInputProps,
  'param' | 'value' | 'onChange' | 'error' | 'isRequired'
>) {
  const { mutateAsync: uploadFile, isPending } = useUploadFile();
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // value is either empty or "#file#<path>"
  const fileName = value.startsWith('#file#') ? value.slice(6) : '';

  const handleFile = useCallback(
    async (file: File) => {
      const uploadName = `capability-inputs/${Date.now()}-${file.name}`;
      const buffer = await file.arrayBuffer();
      await uploadFile({
        name: uploadName,
        content: buffer,
        ignoreSnackbar: false,
      });
      onChange(`#file#${uploadName}`);
    },
    [uploadFile, onChange]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleClear = () => {
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="text-chariot-text-primary text-sm font-medium">
          {param.name}
          {isRequired && (
            <span className="text-chariot-status-critical ml-0.5">*</span>
          )}
        </label>
        {param.description && (
          <Tooltip
            title={param.description}
            placement="top"
            tooltipClassName="max-w-sm whitespace-normal break-words"
          >
            <InformationCircleIcon className="text-chariot-stroke size-4" />
          </Tooltip>
        )}
      </div>

      {fileName ? (
        <div
          className={twMerge(
            'border-chariot-stroke bg-chariot-background-primary flex items-center gap-2 rounded border p-2',
            error && 'border-chariot-status-critical'
          )}
        >
          <DocumentIcon className="text-chariot-text-secondary size-5 shrink-0" />
          <span className="text-chariot-text-primary min-w-0 flex-1 truncate text-sm">
            {fileName.split('/').pop()}
          </span>
          <button
            type="button"
            onClick={handleClear}
            className="text-chariot-text-secondary hover:text-chariot-text-primary shrink-0"
            aria-label="Remove file"
          >
            <XMarkIcon className="size-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          disabled={isPending}
          className={twMerge(
            'border-chariot-stroke bg-chariot-background-primary flex flex-col items-center gap-1.5 rounded border border-dashed p-4 transition-colors',
            dragOver && 'border-chariot-primary bg-chariot-primary/5',
            error && 'border-chariot-status-critical',
            isPending && 'cursor-wait opacity-60'
          )}
          aria-label={`${param.name} file upload${isRequired ? ' (required)' : ''}`}
        >
          <ArrowUpTrayIcon className="text-chariot-text-secondary size-5" />
          <span className="text-chariot-text-secondary text-sm">
            {isPending ? 'Uploading...' : 'Drop a file here or click to browse'}
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      {error && <p className="text-chariot-status-critical text-xs">{error}</p>}
    </div>
  );
}

export function ParameterInput({
  param,
  value,
  onChange,
  error,
  readOnly = false,
  isRequired = false,
  onBlur,
}: ParameterInputProps) {
  const [expanded, setExpanded] = useState(!!value);
  const yamlField = isYamlField(param);

  if (param.type === 'file' && !readOnly) {
    return (
      <FileUploadInput
        param={param}
        value={value}
        onChange={onChange}
        error={error}
        isRequired={isRequired}
      />
    );
  }

  if (readOnly) {
    return (
      <div className="flex items-center gap-4">
        <div
          className={`${isRequired ? 'max-w-56 min-w-48' : 'max-w-56 min-w-40'} shrink-0 truncate`}
        >
          <span className="text-chariot-text-primary text-sm font-bold">
            {param.name}
          </span>
        </div>
        <div className="flex-1">
          <span className="text-chariot-text-secondary text-sm">
            {(value || param.default || 'Not configured').split(',').join(', ')}
          </span>
        </div>
      </div>
    );
  }

  if (yamlField) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <label className="text-chariot-text-primary text-sm font-medium">
            {param.name}
            {isRequired && (
              <span className="text-chariot-status-critical ml-0.5">*</span>
            )}
          </label>
          {param.description && (
            <Tooltip
              title={param.description}
              placement="top"
              tooltipClassName="max-w-sm whitespace-normal break-words"
            >
              <InformationCircleIcon className="text-chariot-stroke size-4" />
            </Tooltip>
          )}
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-chariot-primary text-xs hover:underline"
            >
              Edit YAML
            </button>
          )}
          {expanded && value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setExpanded(false);
              }}
              className="text-chariot-status-critical text-xs hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        {expanded && (
          <Suspense
            fallback={
              <div className="bg-chariot-background-primary border-chariot-stroke text-chariot-text-secondary h-48 rounded border p-2 text-sm">
                Loading editor...
              </div>
            }
          >
            <div
              className={`overflow-hidden rounded border ${
                error
                  ? 'border-chariot-status-critical'
                  : 'border-chariot-stroke'
              }`}
            >
              <MonacoEditor
                content={value}
                onChange={onChange}
                validator={null}
                height="200px"
              />
            </div>
          </Suspense>
        )}
        {error && (
          <p className="text-chariot-status-critical text-xs">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex max-w-56 min-w-40 shrink-0 items-center gap-2">
        <label className="text-chariot-text-primary text-sm font-medium">
          {param.name}
          {isRequired && (
            <span className="text-chariot-status-critical ml-0.5">*</span>
          )}
        </label>
        {param.description && (
          <Tooltip
            title={param.description}
            placement="top"
            tooltipClassName="max-w-sm whitespace-normal break-words"
          >
            <InformationCircleIcon className="text-chariot-stroke size-4" />
          </Tooltip>
        )}
      </div>
      <div className="flex-1">
        {param.type === 'bool' ? (
          <Select
            value={value || param.default || 'false'}
            onValueChange={onChange}
          >
            <SelectTrigger
              aria-label={`${param.name} select${isRequired ? ' (required)' : ''}`}
              className={error ? 'border-chariot-status-critical' : ''}
              onBlur={() => onBlur?.(param.name)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">Disabled</SelectItem>
              <SelectItem value="true">Enabled</SelectItem>
            </SelectContent>
          </Select>
        ) : param.type === '[]string' &&
          param.options &&
          param.options.length > 0 ? (
          <MultiSelectCheckboxes
            options={param.options}
            value={value}
            onChange={onChange}
            paramName={param.name}
            isRequired={isRequired}
            error={error}
          />
        ) : param.options &&
          param.options.length > 0 &&
          isCommaSeparated(param) ? (
          <MultiSelectDropdown
            param={param}
            value={value}
            onChange={onChange}
            error={error}
          />
        ) : param.options && param.options.length > 0 ? (
          <Select
            value={value || CLEAR_SENTINEL}
            onValueChange={v => onChange(v === CLEAR_SENTINEL ? '' : v)}
          >
            <SelectTrigger
              aria-label={`${param.name} select${isRequired ? ' (required)' : ''}`}
              className={error ? 'border-chariot-status-critical' : ''}
              onBlur={() => onBlur?.(param.name)}
            >
              <SelectValue
                placeholder={`Select ${param.name.toLowerCase()}...`}
              />
            </SelectTrigger>
            <SelectContent>
              {!isRequired && (
                <SelectItem value={CLEAR_SENTINEL}>
                  Select {param.name.toLowerCase()}...
                </SelectItem>
              )}
              {param.options.map(opt => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : isBooleanParam(param) ? (
          <Select
            value={value || CLEAR_SENTINEL}
            onValueChange={v => onChange(v === CLEAR_SENTINEL ? '' : v)}
          >
            <SelectTrigger
              aria-label={`${param.name} select${isRequired ? ' (required)' : ''}`}
              className={error ? 'border-chariot-status-critical' : ''}
              onBlur={() => onBlur?.(param.name)}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {!isRequired && (
                <SelectItem value={CLEAR_SENTINEL}>Select...</SelectItem>
              )}
              <SelectItem value="false">false</SelectItem>
              <SelectItem value="true">true</SelectItem>
            </SelectContent>
          </Select>
        ) : isIntParam(param) ? (
          <input
            type="number"
            name={param.name}
            min="0"
            step="1"
            placeholder={
              param.default || `Enter ${param.name.toLowerCase()}...`
            }
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={() => onBlur?.(param.name)}
            className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary block w-full rounded-[2px] border p-1.5 pl-3 outline-0 focus:ring-1 focus:outline-hidden ${error ? 'border-chariot-status-critical' : ''}`}
            aria-label={`${param.name} input${isRequired ? ' (required)' : ''}`}
          />
        ) : (
          <Input
            name={param.name}
            type={isPasswordField(param) ? Type.PASSWORD : undefined}
            password={isPasswordField(param)}
            placeholder={
              param.default || `Enter ${param.name.toLowerCase()}...`
            }
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={() => onBlur?.(param.name)}
            error={error}
            className="w-full"
            aria-label={`${param.name} input${isRequired ? ' (required)' : ''}`}
          />
        )}
      </div>
    </div>
  );
}
